/**
 * Push permission — the states a person can actually be in, named for what we
 * tell them rather than for what the browser API returns.
 *
 *   checking          — still asking the browser; render nothing decisive yet.
 *   on                — permission granted; this device receives pushes.
 *   ask               — never asked (or dismissed); we can show the prompt.
 *   blocked           — denied; only the browser/OS settings can undo it, so
 *                       the UI explains WHERE, instead of a button that does nothing.
 *   ios-needs-install — iPhone/iPad Safari in a tab: Web Push only exists for a
 *                       Home Screen app (iOS 16.4+), so the next step is
 *                       "Add to Home Screen", not "Allow".
 *   unsupported       — this browser has no Web Push at all.
 */
export type PushState = 'checking' | 'on' | 'ask' | 'blocked' | 'ios-needs-install' | 'unsupported';

export type PushPlatform = 'web' | 'android' | 'ios';

export interface PushEnvironment {
  readonly messagingSupported: boolean;
  /** `Notification.permission`, or null when the Notification API is absent. */
  readonly permission: 'default' | 'granted' | 'denied' | null;
  readonly isIos: boolean;
  /** Launched from the Home Screen (display-mode standalone / navigator.standalone). */
  readonly isStandalone: boolean;
}

/** Pure: map what the browser reports to the state we show. */
export function derivePushState(envr: PushEnvironment): PushState {
  // iOS first: a Safari tab reports "unsupported", but the honest answer is
  // that it WOULD work once installed — that is a next step, not a dead end.
  if (envr.isIos && !envr.isStandalone) return 'ios-needs-install';
  if (!envr.messagingSupported || envr.permission === null) return 'unsupported';
  if (envr.permission === 'granted') return 'on';
  if (envr.permission === 'denied') return 'blocked';
  return 'ask';
}

export function detectPlatform(userAgent: string): PushPlatform {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'web';
}

/** iPadOS 13+ reports a Mac user agent; touch points give it away. */
export function detectIos(userAgent: string, maxTouchPoints: number): boolean {
  if (/iphone|ipad|ipod/i.test(userAgent)) return true;
  return /macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

/**
 * Firestore doc ids can't contain '/', and `.`/`#`/`$`/`[`/`]` are best
 * avoided; FCM tokens can contain some of them. Same rule the customer app has
 * always used, so existing token docs keep their ids.
 */
export function tokenDocId(token: string): string {
  return token.replace(/[/.#$[\]]/g, '_').slice(0, 1500);
}

/** Read the live environment. Browser-only; safe to call during an effect. */
export function readPushEnvironment(messagingSupported: boolean): PushEnvironment {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const ua = nav?.userAgent ?? '';
  const standaloneFlag =
    nav !== null && 'standalone' in nav && (nav as { standalone?: unknown }).standalone === true;
  const displayStandalone =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const permission =
    typeof Notification !== 'undefined'
      ? (Notification.permission as 'default' | 'granted' | 'denied')
      : null;
  return {
    messagingSupported,
    permission,
    isIos: detectIos(ua, nav?.maxTouchPoints ?? 0),
    isStandalone: standaloneFlag || displayStandalone,
  };
}
